import { type AdapterStatus } from '@/hooks/admin/useAdminAdapterHealth';
import { AdminTabShell } from './AdminTabShell';

interface AdapterHealthTabProps {
  data: AdapterStatus[] | null;
  isLoading: boolean;
  error: Error | null;
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const ms = Date.now() - date.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function ProportionBar({ online, stale, offline, total }: { online: number; stale: number; offline: number; total: number }) {
  if (total === 0) return null;
  const onlinePct = (online / total) * 100;
  const stalePct = (stale / total) * 100;
  const offlinePct = (offline / total) * 100;
  return (
    <div className="flex h-2 rounded-full overflow-hidden bg-wt-bg-tertiary">
      {onlinePct > 0 && <div className="bg-wt-green-primary" style={{ width: `${onlinePct}%` }} />}
      {stalePct > 0 && <div className="bg-wt-amber-primary" style={{ width: `${stalePct}%` }} />}
      {offlinePct > 0 && <div className="bg-wt-red-primary" style={{ width: `${offlinePct}%` }} />}
    </div>
  );
}

function AdapterCard({ adapter }: { adapter: AdapterStatus }) {
  return (
    <div className="bg-wt-bg-secondary border border-wt-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-wt-text-primary">{adapter.displayName}</h3>
        <span className="text-xs font-mono text-wt-text-muted">{adapter.totalSystems} systems</span>
      </div>

      <ProportionBar
        online={adapter.onlineSystems}
        stale={adapter.staleSystems}
        offline={adapter.offlineSystems}
        total={adapter.totalSystems}
      />

      <div className="grid grid-cols-4 gap-2 text-center">
        <div>
          <div className="text-lg font-mono text-wt-green-primary">{adapter.onlineSystems}</div>
          <div className="text-[10px] uppercase text-wt-text-muted">Online</div>
        </div>
        <div>
          <div className="text-lg font-mono text-wt-amber-primary">{adapter.staleSystems}</div>
          <div className="text-[10px] uppercase text-wt-text-muted">Stale</div>
        </div>
        <div>
          <div className="text-lg font-mono text-wt-red-primary">{adapter.offlineSystems}</div>
          <div className="text-[10px] uppercase text-wt-text-muted">Offline</div>
        </div>
        <div>
          <div className={`text-lg font-mono ${adapter.authFailedSystems > 0 ? 'text-wt-red-primary' : 'text-wt-text-muted'}`}>
            {adapter.authFailedSystems}
          </div>
          <div className="text-[10px] uppercase text-wt-text-muted">Auth Fail</div>
        </div>
      </div>

      <div className="flex justify-between text-xs text-wt-text-muted pt-1 border-t border-wt-border">
        <span>Avg poll age: <span className="font-mono text-wt-text-secondary">{adapter.avgMinutesSincePoll?.toFixed(0) ?? '--'} min</span></span>
        <span>Last OK: <span className="font-mono text-wt-text-secondary">{formatRelativeTime(adapter.lastSuccessfulPoll)}</span></span>
      </div>
    </div>
  );
}

export function AdapterHealthTab({ data, isLoading, error }: AdapterHealthTabProps) {
  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {data.length === 0 ? (
            <p className="text-sm text-wt-text-muted py-8 text-center">No adapter data available.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.map((adapter) => (
                <AdapterCard key={adapter.provider} adapter={adapter} />
              ))}
            </div>
          )}
        </div>
      )}
    </AdminTabShell>
  );
}
